import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { PageSpinner } from '../common/LoadingSpinner';
import SiteFooter from '../common/SiteFooter';
import { saveReturnTo, takeReturnTo } from '../../utils/returnTo';

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated, remembering the page a logged-out visitor asked
 * for so sign-in can return to it.
 */
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  // Once this page has been shown signed in, a flip to logged out is a sign-out, not a visit:
  // don't save the page, so the next sign-in lands where it always has.
  const wasAuthenticated = useRef(false);
  if (isAuthenticated) wasAuthenticated.current = true;

  if (isLoading) {
    return <PageSpinner text="Loading..." />;
  }

  if (!isAuthenticated) {
    if (!wasAuthenticated.current) {
      saveReturnTo(location.pathname + location.search + location.hash);
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-auth-shell">
      {children}
      <SiteFooter minimal />
    </div>
  );
};

/** Sends a just-signed-in user to the saved page, or /dashboard. Navigates once, even under StrictMode. */
const SignedInRedirect = () => {
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    navigate(takeReturnTo() ?? '/dashboard', { replace: true });
  }, [navigate]);

  return null;
};

/**
 * Public Route Component
 * Redirects to the saved page (or dashboard) if user is already authenticated
 */
export const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageSpinner text="Loading..." />;
  }

  return !isAuthenticated ? <>{children}</> : <SignedInRedirect />;
};
